import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Plus, Pencil, Trash2, Download } from "lucide-react";

export default function PreviewPage() {
  return (
    <div className="min-h-screen bg-background p-6 text-foreground">
      <h1 className="mb-2 text-3xl font-bold">Ultramesure</h1>
      <p className="mb-8 text-muted-foreground">Design System Preview — TikTok Sans + Violet/Indigo</p>

      {/* Typographie */}
      <section className="mb-10">
        <h2 className="mb-4 border-b border-border pb-2 text-lg font-semibold">Typographie — TikTok Sans</h2>
        <div className="space-y-3">
          <p className="text-3xl font-bold">Heading 1 — Diagnostic corrosion</p>
          <p className="text-2xl font-semibold">Heading 2 — Réseau AEP Lyon 3e</p>
          <p className="text-xl font-medium">Heading 3 — Zone A — Sous-sol</p>
          <p className="text-lg">Heading 4 — Campagne 12/03/2026</p>
          <p className="text-base">Body — Mesure du diamètre extérieur sur canalisation fonte DN150.</p>
          <p className="text-sm text-muted-foreground">Small — Dernière modification il y a 2 heures</p>
          <p className="text-xs text-muted-foreground">Caption — ID: MSR-2026-0042</p>
          <p className="font-mono text-sm">Mono — 88.9mm / 7.2mm / 19.2%</p>
        </div>
      </section>

      {/* Boutons — toutes tailles */}
      <section className="mb-10">
        <h2 className="mb-4 border-b border-border pb-2 text-lg font-semibold">Boutons — Tailles</h2>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col items-center gap-1">
            <Button size="sm">Small (36px)</Button>
            <span className="text-xs text-muted-foreground">sm</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Button>Default (48px)</Button>
            <span className="text-xs text-muted-foreground">default</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Button size="lg">Large (56px)</Button>
            <span className="text-xs text-muted-foreground">lg</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Button size="icon"><Plus /></Button>
            <span className="text-xs text-muted-foreground">icon</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Button size="icon-sm"><Plus /></Button>
            <span className="text-xs text-muted-foreground">icon-sm</span>
          </div>
        </div>
      </section>

      {/* Boutons — variants */}
      <section className="mb-10">
        <h2 className="mb-4 border-b border-border pb-2 text-lg font-semibold">Boutons — Variants</h2>
        <div className="flex flex-wrap gap-3">
          <Button>Contained</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="destructive">Destructive</Button>
          <Button variant="link">Link</Button>
        </div>
      </section>

      {/* Boutons — avec icônes */}
      <section className="mb-10">
        <h2 className="mb-4 border-b border-border pb-2 text-lg font-semibold">Boutons — Avec icônes</h2>
        <div className="flex flex-wrap gap-3">
          <Button><Plus /> Ajouter</Button>
          <Button variant="outline"><Pencil /> Modifier</Button>
          <Button variant="destructive"><Trash2 /> Supprimer</Button>
          <Button variant="ghost"><Download /> Exporter</Button>
        </div>
      </section>

      {/* Boutons — états */}
      <section className="mb-10">
        <h2 className="mb-4 border-b border-border pb-2 text-lg font-semibold">Boutons — États</h2>
        <div className="flex flex-wrap gap-3">
          <Button>Enabled</Button>
          <Button disabled>Disabled</Button>
          <Button loading>Processing</Button>
          <Button variant="outline" loading>Processing</Button>
          <Button variant="ghost" loading>Processing</Button>
        </div>
      </section>

      {/* Couleurs corrosion */}
      <section className="mb-10">
        <h2 className="mb-4 border-b border-border pb-2 text-lg font-semibold">Codes couleur corrosion</h2>
        <div className="flex gap-3">
          <div className="flex h-16 w-24 items-center justify-center rounded-md bg-corrosion-green text-xs font-medium text-white">
            0-19%
          </div>
          <div className="flex h-16 w-24 items-center justify-center rounded-md bg-corrosion-orange text-xs font-medium text-white">
            20-29%
          </div>
          <div className="flex h-16 w-24 items-center justify-center rounded-md bg-corrosion-red text-xs font-medium text-white">
            30-39%
          </div>
          <div className="flex h-16 w-24 items-center justify-center rounded-md bg-corrosion-dark text-xs font-medium text-white">
            &ge;40%
          </div>
        </div>
      </section>

      {/* Badges */}
      <section className="mb-10">
        <h2 className="mb-4 border-b border-border pb-2 text-lg font-semibold">Badges</h2>
        <div className="flex flex-wrap gap-2">
          <Badge>Default</Badge>
          <Badge variant="secondary">Secondary</Badge>
          <Badge variant="outline">Outline</Badge>
          <Badge variant="destructive">Destructive</Badge>
        </div>
      </section>

      {/* Input + Label */}
      <section className="mb-10">
        <h2 className="mb-4 border-b border-border pb-2 text-lg font-semibold">Input &amp; Label</h2>
        <div className="flex max-w-sm flex-col gap-2">
          <Label htmlFor="demo">Diamètre extérieur (mm)</Label>
          <Input id="demo" type="number" placeholder="Ex : 88.9" />
        </div>
      </section>

      {/* Card */}
      <section className="mb-10">
        <h2 className="mb-4 border-b border-border pb-2 text-lg font-semibold">Cards</h2>
        <div className="flex gap-4">
          <Card className="w-64">
            <CardHeader>
              <CardTitle>Site Exemple</CardTitle>
              <CardDescription>Client : Veolia — Lyon 3e</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">3 zones · 42 mesures</p>
            </CardContent>
          </Card>
          <Card className="w-64" size="sm">
            <CardHeader>
              <CardTitle>Zone A — Sous-sol</CardTitle>
              <CardDescription>Campagne 12/03/2026</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">12 points · 2 alertes</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Shadows */}
      <section className="mb-10">
        <h2 className="mb-4 border-b border-border pb-2 text-lg font-semibold">Shadows</h2>
        <div className="flex gap-4">
          <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
            <p className="text-sm text-card-foreground">shadow-sm</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 shadow-md">
            <p className="text-sm text-card-foreground">shadow-md</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 shadow-lg">
            <p className="text-sm text-card-foreground">shadow-lg</p>
          </div>
        </div>
      </section>

      {/* Radius */}
      <section className="mb-10">
        <h2 className="mb-4 border-b border-border pb-2 text-lg font-semibold">Radius</h2>
        <div className="flex items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-sm border border-border bg-muted text-xs">sm</div>
          <div className="flex h-14 w-14 items-center justify-center rounded-md border border-border bg-muted text-xs">md</div>
          <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-muted text-xs">lg</div>
          <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-border bg-muted text-xs">xl</div>
          <div className="flex h-14 w-14 items-center justify-center rounded-full border border-border bg-muted text-xs">full</div>
        </div>
      </section>
    </div>
  );
}
